// How TUNE Analytics says times, sizes and devices: in Australian English, in plain words.
import type { components } from '@tune/shared';

type Device = components['schemas']['Device'];

/** A day as people here read it: "8 Oct 2026", or "8 October 2026" with `long`. */
export function dayText(iso: string, long = false): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: long ? 'long' : 'short', year: 'numeric' });
}

/** A time of day: "3:42 pm". */
export function clockText(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
}

/** A day and time: "8 Oct 2026, 3:42 pm"; today's and yesterday's say so. */
export function whenText(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  const today = new Date(now);
  const yesterday = new Date(now - 86_400_000);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(d, today)) {
    return `Today, ${clockText(iso)}`;
  }
  if (same(d, yesterday)) {
    return `Yesterday, ${clockText(iso)}`;
  }
  return `${dayText(iso)}, ${clockText(iso)}`;
}

/** How long ago: "just now", "2 minutes ago", "yesterday", "3 months ago". */
export function agoText(iso: string, now = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  if (Math.abs(seconds) < 60) {
    return 'just now';
  }
  const words = new Intl.RelativeTimeFormat('en-AU', { numeric: 'auto' });
  const steps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
  ];
  let value = seconds;
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) {
      return words.format(value, unit);
    }
    value = Math.round(value / size);
  }
  return words.format(value, 'year');
}

/** Good morning, afternoon or evening, by the person's own clock. */
export function greeting(now = new Date()): string {
  const hour = now.getHours();
  return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
}

/**
 * A device as the owner knows it. A TUNE MFD by its name; any other by the name its owner gave it, never by its model,
 * and "Other device" when it has none of its own.
 */
export function deviceName(d: Pick<Device, 'kind' | 'name'>): string {
  const name = d.name.trim();
  if (d.kind === 'mfd') {
    return name || 'TUNE MFD';
  }
  return !name || /^mac$/i.test(name) ? 'Other device' : name;
}

/**
 * A device a record names by its ID and name alone: as the owner knows it while it's signed in, and otherwise by its
 * name, never a bare "Mac".
 */
export function deviceRefName(ref: { id: string; name: string }, devices: readonly Device[]): string {
  const known = devices.find((d) => d.id === ref.id);
  if (known) {
    return deviceName(known);
  }
  const name = ref.name.trim();
  return !name || /^mac$/i.test(name) ? 'a device' : name;
}

/** A lap time as racers read it: "1:14.600". */
export function lapText(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(3).padStart(6, '0')}`;
}

export function isMfd(d: Pick<Device, 'kind'>): boolean {
  return d.kind === 'mfd';
}

/** When any of these MFDs last sent anything, or null. */
export function lastSynced(devices: ReadonlyArray<Pick<Device, 'kind' | 'lastSeenAt'>>): string | null {
  return (
    devices
      .filter(isMfd)
      .map((d) => d.lastSeenAt)
      .filter((t): t is string => t !== null)
      .sort()
      .at(-1) ?? null
  );
}

/** A number of things: "1 car", "3 cars". */
export function count(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;
}
